import { faExclamationCircle, faUsersCrown } from '@fortawesome/pro-regular-svg-icons'
import React, { useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { Column } from 'react-table'
import { useQuery } from 'react-query'

import customerUserService from '../services/customerUsersService'
import { useParamsToObj } from '../hooks/useParamsToObj'
import { useUser } from '../hooks/useUser'
import { CustomerUser } from '../types/customer-user'

import EmptyContent from '../components/EmptyContent'
import ErrorPage from '../components/ErrorPage'
import PageHeader from '../components/PageHeader'
import Table from '../components/Table'
import TableSkeleton from '../components/TableSkeleton'
import UserDrawer from '../components/Customers/UserDrawer'
import UserFilterDrawer from '../components/Users/UserFilterDrawer'

const Users: React.FC = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const urlParams = useParamsToObj(['search', 'roles', 'locations'])
  const user = useUser()
  const { data, isLoading, isError } = useQuery(
    ['customerUsers', user.data?.customerUser?.customer?.id, urlParams],
    () => customerUserService.getAllFromCustomerId({ id: user.data?.customerUser?.customer?.id }, urlParams),
    {
      enabled: !!user.data?.customerUser?.customer?.id,
    }
  )

  const [selectedUser, setSelectedUser] = useState<CustomerUser>()
  const [filterDrawerStatus, setFilterDrawerStatus] = useState(false)
  const [itemDrawerStatus, setItemDrawerStatus] = useState(false)

  useEffect(() => {
    if (!itemDrawerStatus) {
      setSelectedUser(undefined)
    }
  }, [itemDrawerStatus])

  const columns = React.useMemo<Column<CustomerUser>[]>(
    () => [
      {
        Header: 'Name',
        accessor: ({ user }) => user?.displayName,
      },
      {
        Header: 'Email',
        accessor: ({ user }) => user?.email,
      },
      {
        Header: 'Role',
        accessor: 'role',
      },
      {
        Header: 'Assigned Locations',
        accessor: ({ assignedPlaces }) =>
          assignedPlaces && assignedPlaces.length > 0 ? (
            <span style={{ maxWidth: 300 }} className="block truncate">
              {assignedPlaces.map(({ name }) => name).join(', ')}
            </span>
          ) : null,
      },
      {
        Header: 'Creation Date',
        accessor: ({ createdAt }) => new Date(createdAt).toLocaleDateString('en-GB'),
      },
      {
        Header: ' ',
        disableSortBy: true,
        accessor: (user) => (
          <button
            onClick={() => {
              setSelectedUser(user)
              setItemDrawerStatus(true)
            }}
            className="text-link"
          >
            View
          </button>
        ),
      },
    ],
    []
  )

  if (isLoading)
    return (
      <div>
        <PageHeader
          title="Users"
          btnText="Add User"
          btnAction={() => setItemDrawerStatus(!itemDrawerStatus)}
          filterBtnAction={() => setFilterDrawerStatus(true)}
          urlParams={urlParams}
          searchPlaceholder="Search Username"
        />
        <TableSkeleton headers={['Name', 'Email', 'Role', 'Assigned Locations', 'Creation Date', ' ']} />
      </div>
    )

  if (isError || !data) {
    return (
      <ErrorPage
        title="Something went wrong!"
        text="We keep track of these errors, but feel free to contact us if refreshing doesn't fix things."
      />
    )
  }

  if (data.length === 0 && !location.search) {
    return (
      <div className="mt-32 flex h-full items-center justify-center">
        <EmptyContent
          title="No Users"
          description="Start by adding a new one."
          icon={faUsersCrown}
          btnAction={() => setItemDrawerStatus(true)}
          btnText="Add User"
        />
      </div>
    )
  }

  return (
    <div>
      <UserFilterDrawer open={filterDrawerStatus} onClose={() => setFilterDrawerStatus(false)} />
      <UserDrawer
        defaultCustomerId={user.data?.customerUser?.customer?.id}
        open={itemDrawerStatus}
        onClose={() => setItemDrawerStatus(false)}
        selectedUser={selectedUser}
      />
      <PageHeader
        title="Users"
        btnText="Add User"
        btnAction={() => setItemDrawerStatus(!itemDrawerStatus)}
        filterBtnAction={() => setFilterDrawerStatus(true)}
        urlParams={urlParams}
        searchPlaceholder="Search Username"
      />
      {data.length > 0 ? (
        <Table columns={columns} data={data} />
      ) : (
        <div className="mt-32 flex h-full items-center justify-center">
          <EmptyContent
            title={`No results found`}
            description="Please change the search terms to match results."
            icon={faExclamationCircle}
            btnAction={() => {
              navigate({
                search: ``,
              })
            }}
            btnText="Reset filters"
          />
        </div>
      )}
    </div>
  )
}

export default Users
